import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Play, Pause, SkipBack, SkipForward, RotateCcw, Maximize2, X } from 'lucide-react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Text, Line, Html } from '@react-three/drei';
import * as THREE from 'three';

// Color palette for items - distinct, muted colors
const ITEM_COLORS = [
  '#4f86f7', '#f7894f', '#4fc784', '#c74f8e', '#f7d94f',
  '#8e4fc7', '#4fc7c7', '#f74f4f', '#7bc74f', '#c7a04f',
  '#4f6fc7', '#c7604f', '#60c74f', '#c74fc0', '#4fc7a0',
];

// Pallet deck height, mirroring PackingConfig.PALLET_DECK_HEIGHT_CM. EPAL 1 is
// 144 mm with a +10/-0 tolerance, so 15 cm covers a deck at its upper bound.
// This is not decoration: since 2026-09-16 the carrier bills the load height
// PLUS this deck, so the thing ops hands over really is this much taller than
// the stack, and the view should say so.
const PALLET_DECK_CM = 15;

// Bubble wrap around a direct-shipped item, mirroring the +4cm/dim the bin card
// prints. The engine hands us the RAW item dims, so without this the 3D box
// would be smaller than the size the card (and the carrier) is quoting.
const DIRECT_PADDING_CM = 4;

// An EPAL-shaped deck drawn UNDER the bin floor: top boards, three bearer
// blocks, bottom runners. Boards run along the longer side, as a real pallet's
// do. Cheap on purpose - about twenty boxes, no textures - because a bin view
// can hold a dozen of these at once.
function PalletDeck({ width, depth, deckHeight = PALLET_DECK_CM, floorY }) {
  const boardT = deckHeight * 0.15;          // board thickness
  const blockH = deckHeight - boardT * 2;    // bearer height between the decks
  const alongW = width >= depth;             // boards span the longer side
  const span = alongW ? width : depth;
  const across = alongW ? depth : width;

  const topBoards = 6, botBoards = 3;
  const boardW = across / (topBoards + (topBoards - 1) * 0.35);
  const gap = boardW * 0.35;
  const botBoardW = across / (botBoards + (botBoards - 1) * 1.6);

  const topY = floorY - boardT / 2;
  const blockY = floorY - boardT - blockH / 2;
  const botY = floorY - boardT - blockH - boardT / 2;

  const place = (a, b) => (alongW ? [a, b] : [b, a]);   // (span, across) -> (x, z)
  const size = (a, b, h) => (alongW ? [a, h, b] : [b, h, a]);

  const wood = '#b98b5e', woodDark = '#8a6440';
  const boards = [];
  for (let i = 0; i < topBoards; i++) {
    const off = -across / 2 + boardW / 2 + i * (boardW + gap);
    boards.push(
      <mesh key={`t${i}`} position={[place(0, off)[0], topY, place(0, off)[1]]}>
        <boxGeometry args={size(span, boardW, boardT)} />
        <meshStandardMaterial color={wood} roughness={0.9} />
      </mesh>
    );
  }
  for (let i = 0; i < botBoards; i++) {
    const off = -across / 2 + botBoardW / 2 + i * (across - botBoardW) / (botBoards - 1);
    boards.push(
      <mesh key={`b${i}`} position={[place(0, off)[0], botY, place(0, off)[1]]}>
        <boxGeometry args={size(span, botBoardW, boardT)} />
        <meshStandardMaterial color={woodDark} roughness={0.95} />
      </mesh>
    );
  }
  // Three rows of three bearer blocks, the shape a forklift enters between.
  const blockS = span * 0.13, blockA = across * 0.16;
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      const sOff = (i - 1) * (span / 2 - blockS / 2);
      const aOff = (j - 1) * (across / 2 - blockA / 2);
      boards.push(
        <mesh key={`k${i}${j}`} position={[place(sOff, aOff)[0], blockY, place(sOff, aOff)[1]]}>
          <boxGeometry args={size(blockS, blockA, blockH)} />
          <meshStandardMaterial color={woodDark} roughness={0.95} />
        </mesh>
      );
    }
  }
  return <group>{boards}</group>;
}

function BinWireframe({ width, height, depth, cutHeight }) {
  const hw = width / 2, hh = height / 2, hd = depth / 2;

  // 12 edges of a box
  const edges = [
    // Bottom face
    [[-hw, -hh, -hd], [hw, -hh, -hd]],
    [[hw, -hh, -hd], [hw, -hh, hd]],
    [[hw, -hh, hd], [-hw, -hh, hd]],
    [[-hw, -hh, hd], [-hw, -hh, -hd]],
    // Top face
    [[-hw, hh, -hd], [hw, hh, -hd]],
    [[hw, hh, -hd], [hw, hh, hd]],
    [[hw, hh, hd], [-hw, hh, hd]],
    [[-hw, hh, hd], [-hw, hh, -hd]],
    // Vertical edges
    [[-hw, -hh, -hd], [-hw, hh, -hd]],
    [[hw, -hh, -hd], [hw, hh, -hd]],
    [[hw, -hh, hd], [hw, hh, hd]],
    [[-hw, -hh, hd], [-hw, hh, hd]],
  ];

  // Cut line: dashed rectangle at y = -hh + cutHeight (height above the bottom)
  // Only show if cutHeight is meaningfully below the box height.
  const showCut = typeof cutHeight === 'number' && cutHeight > 0 && cutHeight < height;
  const cutY = -hh + cutHeight;
  const cutEdges = showCut ? [
    [[-hw, cutY, -hd], [hw, cutY, -hd]],
    [[hw, cutY, -hd], [hw, cutY, hd]],
    [[hw, cutY, hd], [-hw, cutY, hd]],
    [[-hw, cutY, hd], [-hw, cutY, -hd]],
  ] : [];

  return (
    <group>
      {edges.map((edge, i) => (
        <Line key={i} points={edge} color="#94a3b8" lineWidth={1.5} dashed={false} />
      ))}
      {/* Semi-transparent bottom */}
      <mesh position={[0, -hh, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[width, depth]} />
        <meshBasicMaterial color="#e2e8f0" transparent opacity={0.15} side={THREE.DoubleSide} />
      </mesh>
      {/* Cut line — dashed teal rectangle at the height the box should be cut to */}
      {showCut && cutEdges.map((edge, i) => (
        <Line key={`cut-${i}`} points={edge} color="#0d9488" lineWidth={2} dashed dashSize={Math.max(width, depth) * 0.03} gapSize={Math.max(width, depth) * 0.02} />
      ))}
      {showCut && (
        <mesh position={[0, cutY, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[width, depth]} />
          <meshBasicMaterial color="#0d9488" transparent opacity={0.08} side={THREE.DoubleSide} />
        </mesh>
      )}
    </group>
  );
}

// How far above its resting place an item starts its drop, so it visibly falls
// into the box rather than popping in.
const dropHeight = (binHeight) => Math.max(binHeight * 0.6, 20);

// progressRef holds the playback position as a float: item `order` is hidden
// below `order`, falls between `order` and `order + 1`, and rests after that.
// It is read every frame and applied to the mesh directly, so playback never
// re-renders React; a static view just parks it at the item count.
function PackedItem({ item, order, total, progressRef, binWidth, binHeight, binDepth, colorIndex, onHover, onUnhover, isHovered }) {
  const groupRef = useRef();
  const matRef = useRef();
  const color = ITEM_COLORS[colorIndex % ITEM_COLORS.length];

  // Items come with x,y,z as position within the bin (origin at corner)
  // We need to center the bin at origin, so offset items
  const x = item.x + item.width / 2 - binWidth / 2;
  const y = item.y + item.height / 2 - binHeight / 2;
  const z = item.z + item.depth / 2 - binDepth / 2;
  const fall = dropHeight(binHeight);

  useFrame(() => {
    const g = groupRef.current;
    if (!g) return;
    const progress = progressRef.current;
    const local = progress - order;
    g.visible = local > 0;
    if (!g.visible) return;
    const t = Math.min(local, 1);
    g.position.y = y + fall * Math.pow(1 - t, 3);
    // The item just placed glows until the next one starts, except once the
    // box is complete: a finished box has no "current" item.
    const current = local <= 1 && progress < total;
    const m = matRef.current;
    if (m) {
      m.emissive.set(isHovered || current ? color : '#000000');
      m.emissiveIntensity = isHovered ? 0.3 : current ? 0.45 : 0;
      m.opacity = isHovered || current ? 0.9 : 0.7;
    }
  });

  return (
    <group ref={groupRef} position={[x, y, z]}>
      <mesh
        onPointerOver={(e) => { e.stopPropagation(); onHover(); }}
        onPointerOut={(e) => { e.stopPropagation(); onUnhover(); }}
      >
        <boxGeometry args={[item.width, item.height, item.depth]} />
        <meshStandardMaterial ref={matRef} color={color} transparent opacity={0.7} />
      </mesh>
      {/* Wireframe overlay */}
      <mesh>
        <boxGeometry args={[item.width, item.height, item.depth]} />
        <meshBasicMaterial color={color} wireframe transparent opacity={0.4} />
      </mesh>
      {/* Tooltip on hover */}
      {isHovered && (
        <Html center distanceFactor={undefined} style={{ pointerEvents: 'none' }}>
          <div className="bg-card border border-border rounded-lg shadow-lg px-3 py-2 text-xs whitespace-nowrap" style={{ transform: 'translateY(-40px)' }}>
            <p className="font-semibold">{item.product_title || item.name}</p>
            {item.variant_title && item.variant_title !== 'Default Title' && (
              <p className="text-muted-foreground">{item.variant_title}</p>
            )}
            <p className="text-muted-foreground font-mono mt-0.5">
              {item.width.toFixed(0)} x {item.height.toFixed(0)} x {item.depth.toFixed(0)} cm
            </p>
          </div>
        </Html>
      )}
    </group>
  );
}

function AxisLabels({ width, height, depth, drop = 0 }) {
  const hw = width / 2, hh = height / 2 + drop, hd = depth / 2;
  const fontSize = Math.max(width, height, depth) * 0.04;

  return (
    <group>
      <Text position={[0, -hh - fontSize * 2, hd + fontSize]} fontSize={fontSize} color="#64748b" anchorX="center">
        {width.toFixed(0)} cm
      </Text>
      <Text position={[hw + fontSize * 2, drop / 2, hd + fontSize]} fontSize={fontSize} color="#64748b" anchorX="center" rotation={[0, 0, Math.PI / 2]}>
        {height.toFixed(0)} cm{drop ? ` + ${drop} pallet` : ''}
      </Text>
      <Text position={[hw + fontSize, -hh - fontSize * 2, 0]} fontSize={fontSize} color="#64748b" anchorX="center" rotation={[0, Math.PI / 2, 0]}>
        {depth.toFixed(0)} cm
      </Text>
    </group>
  );
}

function Scene({ bin, items, cutHeight, isPallet, progressRef, playing, stepSeconds, speed, onStep, onFinish }) {
  const binW = bin.width;
  const binH = bin.height;
  const binD = bin.depth;
  const [hoveredItem, setHoveredItem] = useState(null);
  const total = items.length;
  const shownRef = useRef(null);

  // Advance playback, and tell the controls when the visible item count moves
  // (only then, so the slider re-renders once per item, not every frame).
  useFrame((_, delta) => {
    if (playing) {
      const next = Math.min(total, progressRef.current + (delta * speed) / stepSeconds);
      progressRef.current = next;
      if (next >= total) onFinish?.();
    }
    const shown = Math.ceil(progressRef.current - 1e-6);
    if (shown !== shownRef.current) {
      shownRef.current = shown;
      onStep?.(shown);
    }
  });

  // Calculate camera distance based on bin size
  const maxDim = Math.max(binW, binH, binD);

  return (
    <>
      <ambientLight intensity={0.6} />
      <directionalLight position={[maxDim, maxDim * 1.5, maxDim]} intensity={0.8} />
      <directionalLight position={[-maxDim * 0.5, maxDim, -maxDim * 0.5]} intensity={0.3} />

      <BinWireframe width={binW} height={binH} depth={binD} cutHeight={cutHeight} />
      {isPallet && <PalletDeck width={binW} depth={binD} floorY={-binH / 2} />}
      <AxisLabels width={binW} height={binH} depth={binD} drop={isPallet ? PALLET_DECK_CM : 0} />

      {items.map(({ item, colorIndex }, idx) => (
        <PackedItem
          key={idx}
          item={item}
          order={idx}
          total={total}
          progressRef={progressRef}
          binWidth={binW}
          binHeight={binH}
          binDepth={binD}
          colorIndex={colorIndex}
          isHovered={hoveredItem === idx}
          onHover={() => setHoveredItem(idx)}
          onUnhover={() => setHoveredItem(null)}
        />
      ))}

      <OrbitControls
        enablePan={true}
        enableZoom={true}
        enableRotate={true}
        autoRotate={playing}
        autoRotateSpeed={0.8}
        minDistance={maxDim * 0.5}
        maxDistance={maxDim * 5}
      />
    </>
  );
}

// A direct-shipping bin never goes through the packer, so its items carry no
// coordinates at all (a taped stack) or a bare 0,0,0 (a single item). Lay them
// out the way ops tapes them: along the one axis the bin is longer than a
// single item, centred in the bubble wrap.
function stackDirectItems(items, rawDims, boxDims) {
  const axes = [['width', 'x'], ['height', 'y'], ['depth', 'z']];
  const gap = (axis) => rawDims[axis] - Math.max(...items.map((it) => it[axis] || 0));
  const [stackAxis] = axes.reduce((a, b) => (gap(b[0]) > gap(a[0]) ? b : a));

  const span = items.reduce((s, it) => s + (it[stackAxis] || 0), 0);
  let cursor = (boxDims[stackAxis] - span) / 2;

  return items.map((item) => {
    const placed = { ...item };
    axes.forEach(([axis, key]) => {
      placed[key] = axis === stackAxis ? cursor : (boxDims[axis] - (item[axis] || 0)) / 2;
    });
    cursor += item[stackAxis] || 0;
    return placed;
  });
}

// Packing order for playback: bottom layer first, then back to front, then
// left to right. Anything an item rests on has a lower y, so the replay never
// shows a box floating before its support arrives. Each entry keeps its
// original index for the colour, so a static view looks exactly as before.
function playbackOrder(items) {
  return items
    .map((item, colorIndex) => ({ item, colorIndex }))
    .sort((a, b) => {
      const dy = a.item.y - b.item.y;
      if (Math.abs(dy) > 0.5) return dy;
      const dz = a.item.z - b.item.z;
      if (Math.abs(dz) > 0.5) return dz;
      return a.item.x - b.item.x;
    });
}

const SPEEDS = [1, 2, 4];

const ctrlBtn = 'inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-card text-muted-foreground hover:bg-muted/50 hover:text-foreground disabled:opacity-40';

function BoxView({ binDims, items, cutHeight, isPallet, animate, autoPlay = false, height = 320, title, expandable }) {
  const total = items.length;
  const progressRef = useRef(autoPlay ? 0 : total);
  const [shown, setShown] = useState(autoPlay ? 0 : total);
  const [playing, setPlaying] = useState(autoPlay);
  const [speed, setSpeed] = useState(1);
  const [expanded, setExpanded] = useState(false);

  // About ten seconds for a whole box at 1x, never slower than half a second
  // per item, never so fast a single drop can't be followed.
  const stepSeconds = Math.min(0.55, Math.max(0.12, 10 / Math.max(total, 1)));

  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);

  const seek = (value) => {
    setPlaying(false);
    progressRef.current = Math.max(0, Math.min(total, value));
    setShown(Math.ceil(progressRef.current - 1e-6));
  };
  const togglePlay = () => {
    if (playing) { setPlaying(false); return; }
    if (progressRef.current >= total) seek(0);
    setPlaying(true);
  };
  const stepBack = () => seek(Math.ceil(progressRef.current - 1e-6) - 1);
  const stepForward = () => seek(Math.floor(progressRef.current + 1e-6) + 1);

  const maxDim = Math.max(binDims.width, binDims.height + (isPallet ? PALLET_DECK_CM : 0), binDims.depth);
  const cameraDistance = maxDim * 1.8;
  const current = shown > 0 && shown < total ? items[shown - 1].item : null;
  const currentName = current ? (current.product_title || current.name) : null;

  return (
    <div className="rounded-lg border border-border bg-muted/10 overflow-hidden">
      <div className="relative" style={{ height }}>
        <Canvas
          camera={{
            position: [cameraDistance * 0.7, cameraDistance * 0.5, cameraDistance * 0.7],
            fov: 45,
            near: 0.1,
            far: maxDim * 20,
          }}
          style={{ background: 'transparent' }}
        >
          <Scene
            bin={binDims}
            items={items}
            cutHeight={cutHeight}
            isPallet={isPallet}
            progressRef={progressRef}
            playing={playing}
            stepSeconds={stepSeconds}
            speed={speed}
            onStep={setShown}
            onFinish={() => setPlaying(false)}
          />
        </Canvas>
        {animate && expandable && (
          <button
            type="button"
            onClick={() => { setPlaying(false); setExpanded(true); }}
            className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md border border-border bg-card/90 px-2 py-1 text-[11px] font-medium text-muted-foreground hover:text-foreground"
            title="Open a large view"
          >
            <Maximize2 className="h-3 w-3" /> Large
          </button>
        )}
        {animate && currentName && (
          <div className="pointer-events-none absolute left-2 top-2 max-w-[70%] truncate rounded-md bg-card/90 px-2 py-1 text-[11px] text-foreground shadow-sm">
            Placing <span className="font-medium">{currentName}</span>
          </div>
        )}
      </div>

      {animate && (
        <div className="flex flex-wrap items-center gap-2 border-t border-border bg-card/60 px-2 py-1.5">
          <button type="button" className={ctrlBtn} onClick={stepBack} disabled={shown <= 0} title="Previous item">
            <SkipBack className="h-3.5 w-3.5" />
          </button>
          <button type="button" className={`${ctrlBtn} text-foreground`} onClick={togglePlay}
            title={playing ? 'Pause' : shown >= total ? 'Replay the packing' : 'Play'}>
            {playing ? <Pause className="h-3.5 w-3.5" /> : shown >= total ? <RotateCcw className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          </button>
          <button type="button" className={ctrlBtn} onClick={stepForward} disabled={shown >= total} title="Next item">
            <SkipForward className="h-3.5 w-3.5" />
          </button>
          <input
            type="range"
            min={0}
            max={total}
            step={1}
            value={shown}
            onChange={(e) => seek(Number(e.target.value))}
            className="h-1 min-w-[80px] flex-1 cursor-pointer accent-primary"
            aria-label="Packing step"
          />
          <span className="font-mono text-[11px] text-muted-foreground tabular-nums whitespace-nowrap">
            item {shown} / {total}
          </span>
          <button
            type="button"
            onClick={() => setSpeed(SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length])}
            className="rounded-md border border-border bg-card px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground hover:text-foreground"
            title="Playback speed"
          >
            {speed}x
          </button>
        </div>
      )}

      {expanded && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-2 sm:p-6"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setExpanded(false); }}
        >
          <div className="relative flex w-full max-w-6xl flex-col rounded-xl border border-border bg-card shadow-2xl">
            <div className="flex items-center gap-3 border-b border-border px-4 py-2.5">
              <p className="mr-auto text-sm font-semibold">
                {title || 'Box'} <span className="font-normal text-muted-foreground">· {total} item{total === 1 ? '' : 's'}</span>
              </p>
              <button type="button" onClick={() => setExpanded(false)} className={ctrlBtn} title="Close (Esc)">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-3">
              <BoxView
                binDims={binDims}
                items={items}
                cutHeight={cutHeight}
                isPallet={isPallet}
                animate
                autoPlay
                height="70vh"
                expandable={false}
              />
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

// `animate` adds the packing replay (play / step / slider / speed) and a large
// view. Off by default, so other pages keep the plain static preview.
export default function PackingVisualization3D({ bin, animate = false }) {
  const dims = bin.bin_dimensions || bin.dimensions;
  if (!dims || !bin.items?.length) return null;

  // Same test the bin card uses, so the box drawn here is the box it prints.
  const isDirect = (bin.strategy || '').includes('direct') || bin.bin_name === 'Direct Shipping';

  const rawDims = {
    width: dims.width,
    height: dims.height,
    depth: dims.length || dims.depth,
  };
  const binDims = isDirect
    ? {
        width: rawDims.width + DIRECT_PADDING_CM,
        height: rawDims.height + DIRECT_PADDING_CM,
        depth: rawDims.depth + DIRECT_PADDING_CM,
      }
    : rawDims;

  const sizedItems = bin.items.filter((item) => item.width != null);
  const positionedItems = isDirect
    ? stackDirectItems(sizedItems, rawDims, binDims)
    : sizedItems.filter((item) => item.x != null && item.y != null && item.z != null);

  if (positionedItems.length === 0) {
    return (
      <div className="mt-3 rounded-lg border border-border bg-muted/10 px-3 py-6 text-center text-xs text-muted-foreground">
        No placement data for this box.
      </div>
    );
  }

  // Show cut line only when cutting actually saves money on a volumetric rate.
  // For weight/per-parcel rates, the cut is irrelevant and would mislead.
  const isVolumetric = /volum/i.test(bin.rate_method || '');
  const showCut = isVolumetric && Number(bin.cut_box_savings || 0) > 0 && bin.used_height != null && bin.used_height < binDims.height;
  const cutHeight = showCut ? bin.used_height : null;

  // A pallet bin (PLT-*, or the synthetic wide base) stands on a deck, so draw
  // one: the carrier bills the load height plus that deck, and a stack floating
  // on nothing has been quietly lying about what leaves the warehouse.
  const isPallet = /^PLT-/i.test(bin.bin_code || bin.bin_name || '')
    || /pallet/i.test(bin.bin_name || '');

  return (
    <div className="mt-3">
      {/* Keyed on the box and its item count: switching strategy tabs reuses
          this component for another box, and the player must start over
          (it showed 'item 16 / 8' after moving from a pallet to a bundle). */}
      <BoxView
        key={`${bin.bin_name || bin.bin_code}-${positionedItems.length}-${binDims.width}x${binDims.height}x${binDims.depth}`}
        binDims={binDims}
        items={playbackOrder(positionedItems)}
        cutHeight={cutHeight}
        isPallet={isPallet}
        animate={animate}
        title={bin.bin_name || bin.bin_code}
        expandable
      />
    </div>
  );
}
